import React from 'react';
import { DropdownMenu, IconButton } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { Event, UiPosition } from '../events';
import type { OrganizationTrackingData } from './types';

const S = {
  Icon: styled(IconButton)`
    margin-left: auto;
  `,
};

const TRANSLATION_DOMAIN = 'account:sites-and-organizations';

export const OrganizationDropdown = (props: {
  trackingData: OrganizationTrackingData;
  hasSites: boolean;
  organizationId: string;
  accountId: string;
  allSitesOnBasic: boolean;
  canDelete: boolean;
  onInvite: () => void;
  onUpdate: () => void;
  onTransfer: () => void;
  onDelete: () => void;
}): React.ReactElement | null => {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const {
    trackingData,
    hasSites,
    organizationId,
    accountId,
    allSitesOnBasic,
    onInvite,
    onUpdate,
    onTransfer,
    onDelete,
    canDelete: canDeleteProp,
  } = props;
  const { click } = useTrack();

  const { hasPermission } = useAuthorization();

  const hasOrgInvitePermission = hasPermission('organization', organizationId, 'org-invite');
  const hasOrgUpdatePermission = hasPermission('organization', organizationId, 'org-update');
  const hasAccountsManagePermission = hasPermission('account', accountId, 'accounts-manage');

  const canInvite = hasSites && hasOrgInvitePermission;
  const canTransfer = allSitesOnBasic && hasSites && hasAccountsManagePermission;
  const canDelete = canDeleteProp && hasAccountsManagePermission;

  if (!canInvite && !hasOrgUpdatePermission && !canTransfer && !canDelete) {
    return null;
  }

  return (
    <DropdownMenu
      trigger={
        <S.Icon
          size="s"
          kind="ghost"
          icon="menu_kebab"
          description={t('organization.actions.description')}
          tooltipText={t('organization.actions.description')}
          onClick={() => {
            click(
              Event.CLICK_ORGINZATION_SETTINGS,
              UiPosition.ORGANIZATION_SETTINGS_MENU,
              trackingData,
            );
          }}
        />
      }
    >
      <DropdownMenu.Item isHidden={!canInvite} onClick={onInvite}>
        {t('organization.actions.invite-people')}
      </DropdownMenu.Item>

      <DropdownMenu.Item isHidden={!hasOrgUpdatePermission} onClick={onUpdate}>
        {t('organization.actions.edit')}
      </DropdownMenu.Item>

      <DropdownMenu.Item isHidden={!canTransfer} onClick={onTransfer}>
        {t('organization.actions.transfer-ownership')}
      </DropdownMenu.Item>

      <DropdownMenu.Item isHidden={!canDelete} onClick={onDelete}>
        {t('organization.actions.delete')}
      </DropdownMenu.Item>
    </DropdownMenu>
  );
};
